import React from 'react';
import {
  Grid, FormControl, InputLabel, Select, MenuItem, OutlinedInput, Chip, Box, TextField,
  Checkbox, FormControlLabel, RadioGroup, Radio, Typography, Stack, Divider,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { sa } from '../../../components/superAdminUi';
import FormSectionHeader from '../../../components/common/FormSectionHeader';
import SectionCard from '../../../components/common/SectionCard';
import { SELECTABLE_PRODUCT_NAMES, enforceSingleEiTier } from '../../../utils/Constants';
import { resolveVatPercent } from '../../../utils/vat';

const TERMS = [
  { id: 1, name: '1 Year' },
  { id: 2, name: '2 Years' },
  { id: 3, name: '3 Years' },
];

const paymentModeName = (mode) => ({ 1: 'invoice', 2: 'card', 3: 'bank' })[mode || 1] || 'invoice';

// Anniversary maths: end date is the day before the term anniversary; renewal is on it.
const anniversary = (start, term, offsetDays = 0) => {
  const date = new Date(start);
  date.setFullYear(date.getFullYear() + term);
  date.setDate(date.getDate() + offsetDays);
  return date;
};

export default function SubscriptionStep({ formik }) {
  const { values, handleChange, setFieldValue } = formik;

  const applyTerm = (term) => {
    setFieldValue('SubscriptionTerm', term);
    if (values.StartDate) {
      setFieldValue('EndDate', anniversary(values.StartDate, term, -1));
      setFieldValue('RenewalDate', anniversary(values.StartDate, term, 0));
    }
  };

  const applyStart = (start) => {
    setFieldValue('StartDate', start);
    if (start && values.SubscriptionTerm) {
      setFieldValue('EndDate', anniversary(start, values.SubscriptionTerm, -1));
      setFieldValue('RenewalDate', anniversary(start, values.SubscriptionTerm, 0));
    }
  };

  const handleProducts = (event) => {
    const { value } = event.target;
    const next = typeof value === 'string' ? value.split(',') : value;
    setFieldValue('products', enforceSingleEiTier(next, values.products || []));
  };

  const year1 = parseFloat(values.Year1Cost || 0);
  const year2 = parseFloat(values.Year2Cost || 0);
  const year3 = parseFloat(values.Year3Cost || 0);
  const discount = parseFloat(values.Discount || 0);
  const subtotal = year1 + year2 + year3 - discount;
  // Honour an explicit 0% VAT (non-UK schools) — see resolveVatPercent.
  const vatPercent = resolveVatPercent(values.VatPercentApplied);
  const vatAmount = subtotal * (vatPercent / 100);
  const total = (subtotal + vatAmount).toFixed(2);

  return (
    <SectionCard title="Subscription" icon={<ReceiptLongOutlinedIcon fontSize="small" />}>
      <Grid container spacing={1.5}>
      <Grid item xs={12} lg={4}>
        <FormControl fullWidth>
          <InputLabel id="register-group-term-label">Subscription Term</InputLabel>
          <Select
            labelId="register-group-term-label"
            label="Subscription Term"
            value={values.SubscriptionTerm}
            onChange={(e) => applyTerm(e.target.value)}
          >
            {TERMS.map((t) => (<MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} lg={4}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Start Date"
            value={values.StartDate || null}
            onChange={applyStart}
            renderInput={(params) => <TextField {...params} fullWidth />}
          />
        </LocalizationProvider>
      </Grid>

      <Grid item xs={12} lg={4}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="End Date"
            value={values.EndDate || null}
            onChange={(v) => setFieldValue('EndDate', v)}
            renderInput={(params) => <TextField {...params} fullWidth />}
          />
        </LocalizationProvider>
      </Grid>

      <Grid item xs={12} lg={4}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Renewal Date"
            value={values.RenewalDate || null}
            onChange={(v) => setFieldValue('RenewalDate', v)}
            renderInput={(params) => <TextField {...params} fullWidth />}
          />
        </LocalizationProvider>
      </Grid>

      <Grid item xs={12}>
        <FormControlLabel
          control={<Checkbox checked={values.IsMultiYear || false} onChange={(e) => setFieldValue('IsMultiYear', e.target.checked)} />}
          label="Multi-Year Agreement"
        />
      </Grid>

      {values.IsMultiYear ? (
        <>
          <Grid item xs={12} lg={4}>
            <TextField fullWidth label="Year 1 Cost (£)" name="Year1Cost" value={values.Year1Cost} onChange={handleChange} />
          </Grid>
          <Grid item xs={12} lg={4}>
            <TextField fullWidth label="Year 2 Cost (£)" name="Year2Cost" value={values.Year2Cost} onChange={handleChange} />
          </Grid>
          <Grid item xs={12} lg={4}>
            <TextField fullWidth label="Year 3 Cost (£)" name="Year3Cost" value={values.Year3Cost} onChange={handleChange} />
          </Grid>
        </>
      ) : (
        <Grid item xs={12} lg={6}>
          <TextField fullWidth label="Annual Cost (£)" name="Year1Cost" value={values.Year1Cost} onChange={handleChange} />
        </Grid>
      )}

      <Grid item xs={12} lg={6}>
        <TextField fullWidth label="Discount (£)" name="Discount" value={values.Discount} onChange={handleChange} />
      </Grid>

      <Grid item xs={12} lg={6}>
        <TextField fullWidth label="VAT (%)" name="VatPercentApplied" value={values.VatPercentApplied} onChange={handleChange} helperText="20% for UK schools, 0% for non-UK" />
      </Grid>

      <Grid item xs={12}>
        <Box sx={{ p: 2, bgcolor: sa.color.rowAlt, borderRadius: sa.radius.md }}>
          <Stack direction="row" justifyContent="space-between"><Typography variant="body2">Subtotal (less discount)</Typography><Typography variant="body2">£{subtotal.toFixed(2)}</Typography></Stack>
          <Stack direction="row" justifyContent="space-between"><Typography variant="body2">VAT</Typography><Typography variant="body2">£{vatAmount.toFixed(2)}</Typography></Stack>
          <Divider sx={{ my: 1 }} />
          <Stack direction="row" justifyContent="space-between"><Typography variant="subtitle2">Final Total</Typography><Typography variant="subtitle2">£{total}</Typography></Stack>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <FormSectionHeader title="Subscription settings" />
      </Grid>
      <Grid item xs={12}>
        <FormControlLabel
          control={<Checkbox checked={values.IsMATSubscription !== undefined ? values.IsMATSubscription : true} onChange={(e) => setFieldValue('IsMATSubscription', e.target.checked)} />}
          label="Apply subscription at group level (MAT-level subscription)"
        />
      </Grid>
      <Grid item xs={12}>
        <FormControlLabel
          control={<Checkbox checked={values.IsAutoRenewal !== undefined ? values.IsAutoRenewal : true} onChange={(e) => setFieldValue('IsAutoRenewal', e.target.checked)} />}
          label="Auto-Renewal"
        />
      </Grid>

      <Grid item xs={12}>
        <FormSectionHeader title="Payment method" />
      </Grid>
      <Grid item xs={12}>
        <FormControl>
          <RadioGroup
            value={paymentModeName(values.PaymentMode)}
            onChange={(e) => setFieldValue('PaymentMode', { invoice: 1, card: 2, bank: 3 }[e.target.value] || 1)}
          >
            <FormControlLabel value="invoice" control={<Radio />} label="Invoice" />
            <FormControlLabel value="card" control={<Radio />} label="Pay via credit or debit card" />
            <FormControlLabel value="bank" control={<Radio />} label="Pay via bank transfer" />
          </RadioGroup>
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <FormSectionHeader title="Products" />
      </Grid>
      <Grid item xs={12}>
        <FormControl fullWidth>
          <InputLabel id="register-group-products-label">Select Products</InputLabel>
          <Select
            labelId="register-group-products-label"
            multiple
            value={values.products || []}
            onChange={handleProducts}
            input={<OutlinedInput label="Select Products" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (<Chip key={value} label={value} />))}
              </Box>
            )}
          >
            {SELECTABLE_PRODUCT_NAMES.map((item) => (
              <MenuItem key={item.id} value={item.name}>{item.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      </Grid>
    </SectionCard>
  );
}
